import AudioMotionAnalyzer from 'audiomotion-analyzer';

// Throttle constant (~50 FPS)
const FRAME_INTERVAL_MS = 1000 / 50;
// Maximum number of points (downsample target)
const MAX_POINTS = 256;

class AudioVisualizer {
  constructor({ containerId, audioEl, height = 400 }) {
    this.containerId = containerId;
    this.audioEl = audioEl;
    this.height = height;
    this.resizeObserver = null;
    // smoothing buffer for bands
    this._amps = null;          // exponential smoothed amplitudes
    this._smoothed = null;      // kernel smoothed amplitudes (reuse buffer)
    // center icon cache
    this.iconImg = null;
    // last draw timestamp for throttling
    this._lastDraw = 0; // last heavy recompute
    this._offscreen = null; // offscreen canvas for stable display
    this._offCtx = null;
    this._gradient = null;       // cached radial gradient
    this._gradKey = '';
    this._silenceFrames = 0;     // consecutive silent heavy-update frames
  }

  init() {
    this.destroy();
    const container = document.getElementById(this.containerId);
    if (!container) {
      console.error(`Container with id ${this.containerId} not found`);
      return;
    }

    // Dimensions
    const rect = container.getBoundingClientRect();
    const currentHeight = Math.max(220, Math.min(this.height, rect.height || this.height));

    // Create analyzer using it as a data source; we'll draw custom rings
    this.audioMotion = new AudioMotionAnalyzer(container, {
      source: null,
      height: currentHeight,
      connectSpeakers: false,
      mode: 1,          // more bands
      barSpace: 0,
      channelLayout: 'single',
      gradient: 'steelblue',
      ledBars: false,
      maxFreq: 20000,
      minFreq: 20,
      mirror: 0,
      radial: false,    // we'll draw our own radial
      showBgColor: false,
      bgAlpha: 0.0,
      showPeaks: false,
      showVolume: false,
      overlay: true,
      start: true,      // animate so onCanvasDraw runs
      showScaleX: false,
      showScaleY: false,
      onCanvasDraw: (inst, info) => this._drawRadial(inst, info),
    });

    // Gentle purple gradient
    this.audioMotion.registerGradient('wavePurple', {
      colorStops: [
        { color: 'rgba(167,139,250,0.5)', pos: 0 },
        { color: 'rgba(124,58,237,0.8)', pos: 0.6 },
        { color: 'rgba(109,40,217,0.5)', pos: 1 },
      ]
    });
    this.audioMotion.setOptions({ gradient: 'wavePurple' });

    // Resize observer
    this.setupResizeObserver();

    // Preload center icon from public
    if (!this.iconImg) {
      this.iconImg = new Image();
      this.iconImg.src = '/icon.svg';
    }
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.audioMotion) {
      this.audioMotion.stop();
      this.audioMotion.disconnectInput();
      this.audioMotion.destroy();
      this.audioMotion = null;
    }
    this._amps = null;
    this._smoothed = null;
    this._offscreen = null;
    this._offCtx = null;
    this._gradient = null;
    this._gradKey = '';
    this._silenceFrames = 0;
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) return;
    const container = document.getElementById(this.containerId);
    if (!container) return;
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (this.audioMotion && (width > 0 && height > 0)) {
          const newHeight = Math.min(this.height, height);
          this.audioMotion.setCanvasSize(width, newHeight);
        }
      }
    });
    this.resizeObserver.observe(container);
  }

  start(stream) {
    if (!stream && !this.audioEl) {
      console.error('No audio source provided. Please provide a stream or an audio element.');
      return;
    }
    const source = stream
      ? this.audioMotion.audioCtx.createMediaStreamSource(stream)
      : this.audioMotion.audioCtx.createMediaElementSource(this.audioEl);
    this.audioMotion.stop();
    this.audioMotion.disconnectInput();
    this.audioMotion.connectInput(source);
    this.audioMotion.start();
  }

  // Early custom radial renderer (low amplitude, no logo)
  _drawRadial(instance, info) {
    const { canvas, canvasCtx: ctx, pixelRatio } = instance;
    const W = canvas.width, H = canvas.height;

    // Ensure offscreen canvas
    if (!this._offscreen || this._offscreen.width !== W || this._offscreen.height !== H) {
      this._offscreen = document.createElement('canvas');
      this._offscreen.width = W;
      this._offscreen.height = H;
      this._offCtx = this._offscreen.getContext('2d');
    }

    const now = info ? info.timestamp : performance.now();
    const doUpdate = (now - this._lastDraw) >= FRAME_INTERVAL_MS;

    if (doUpdate) {
      this._lastDraw = now;
      const bars = instance.getBars();
      if (!bars || !bars.length) {
        ctx.clearRect(0, 0, W, H);
        return;
      }
      // Downsample bars to fixed-length array
      const srcLen = bars.length;
      let step = 1;
      let N = srcLen;
      if (srcLen > MAX_POINTS) {
        step = srcLen / MAX_POINTS;
        N = MAX_POINTS;
      }
      if (!this._amps || this._amps.length !== N) {
        this._amps = new Float32Array(N).fill(0);
        this._smoothed = new Float32Array(N);
      }
      const alpha = 0.18; // smoothing factor
      let peak = 0;
      for (let i = 0; i < N; i++) {
        let eased;
        if (step === 1) {
          const v = bars[i].value[0] ?? bars[i].value ?? 0;
          eased = Math.pow(Math.max(0, Math.min(1, v)), 0.9);
        } else {
          const start = Math.floor(i * step);
          const end = Math.floor((i + 1) * step);
          let sum = 0, c = 0;
          for (let j = start; j < end; j++) {
            const v = bars[j].value[0] ?? bars[j].value ?? 0;
            sum += Math.pow(Math.max(0, Math.min(1, v)), 0.9);
            c++;
          }
          eased = c ? sum / c : 0;
        }
        const sm = this._amps[i] = this._amps[i] * (1 - alpha) + eased * alpha;
        if (sm > peak) peak = sm;
      }

      // Silence detection threshold (tweak as needed)
      const SILENCE_THRESHOLD = 0.008;
      const isSilent = peak < SILENCE_THRESHOLD;
      if (isSilent) {
        this._silenceFrames++;
      } else {
        this._silenceFrames = 0;
      }

      const off = this._offCtx;
      // Only clear & redraw rings if not deep silence or in initial few silent frames (for fade-out)
      const shouldRedraw = !isSilent || this._silenceFrames < 10;
      if (!shouldRedraw) {
        // Skip heavy drawing; reuse last frame
        // Fall through to blit section
      } else {
        off.clearRect(0, 0, W, H);
        const cx = W / 2, cy = H / 2;
        const minDim = Math.min(W, H);

        // radii/density
        const padding = Math.max(8, minDim * 0.1);
        const rOuter0 = Math.max(16, minDim / 2 - padding);
        const rInner0 = Math.max(20, minDim * 0.12);
        let ringCount = Math.max(20, Math.min(18, Math.floor((rOuter0 - rInner0) / (minDim * 0.012))));
        if (ringCount < 10) ringCount = 10;
        const ringGapOrig = (rOuter0 - rInner0) / Math.max(1, (ringCount - 1));
        const ringGap = ringGapOrig / 1.4;
        const radiusScale = 0.8;
        const rInner = rInner0 * radiusScale;
        const rOuter = rOuter0 * radiusScale;
        const baseStart = rOuter - ringGap * (ringCount - 1);

        // N already defined (downsampled length)
        // Kernel smoothing (reuse buffer)
        const kernel = [0.05, 0.1, 0.15, 0.1, 0.05]; // fixed 5-tap kernel
        const smoothed = this._smoothed;
        for (let i = 0; i < N; i++) {
          let acc = 0;
          acc += this._amps[(i - 2 + N) % N] * kernel[0];
          acc += this._amps[(i - 1 + N) % N] * kernel[1];
          acc += this._amps[i] * kernel[2];
          acc += this._amps[(i + 1) % N] * kernel[3];
          acc += this._amps[(i + 2) % N] * kernel[4];
          smoothed[i] = acc; // kernel already sums to 0.45; relative shape fine
        }

        // Gradient cache (depends on size & radii)
        const gKey = `${W}x${H}:${rInner.toFixed(1)}-${rOuter.toFixed(1)}`;
        if (this._gradKey !== gKey) {
          this._gradient = off.createRadialGradient(cx, cy, rInner, cx, cy, rOuter);
          this._gradient.addColorStop(0, 'rgba(167,139,250,0.5)');
          this._gradient.addColorStop(0.6, 'rgba(124,58,237,0.8)');
          this._gradient.addColorStop(1, 'rgba(109,40,217,0.5)');
          this._gradKey = gKey;
        }

        off.lineCap = 'round';
        off.lineJoin = 'round';
        const ampGain = 2.2;
        const t = now * 0.0002;
        const rotation = t % (Math.PI * 2);
        const overshoot = 10 * Math.max(2, minDim * 0.012);

        const pointAt = (i, L) => {
          const a = (i / N) * Math.PI * 2 + rotation;
          const base = baseStart + L * ringGap;
          const tL = (L / (ringCount - 1));
          const damping = tL;
          const allowed = Math.max(0, (rOuter + overshoot) - base);
          const displacement = Math.min(allowed, allowed * Math.min(1, smoothed[i] * ampGain) * damping);
          const r = base + displacement;
          return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
        };

        const lineBase = Math.max(1, Math.min(2, minDim / 640));
        for (let L = 0; L < ringCount; L++) {
          off.beginPath();
          let [sx, sy] = pointAt(0, L);
          off.moveTo(sx, sy);
          for (let i = 0; i < N; i++) {
            const i0 = (i - 1 + N) % N;
            const i1 = i;
            const i2 = (i + 1) % N;
            const i3 = (i + 2) % N;
            const [p0x, p0y] = pointAt(i0, L);
            const [p1x, p1y] = pointAt(i1, L);
            const [p2x, p2y] = pointAt(i2, L);
            const [p3x, p3y] = pointAt(i3, L);
            const c1x = p1x + (p2x - p0x) / 6;
            const c1y = p1y + (p2y - p0y) / 6;
            const c2x = p2x - (p3x - p1x) / 6;
            const c2y = p2y - (p3y - p1y) / 6;
            off.bezierCurveTo(c1x, c1y, c2x, c2y, p2x, p2y);
          }
          off.closePath();
          off.strokeStyle = this._gradient;
          off.shadowBlur = 4 * pixelRatio;
          off.shadowColor = 'rgba(124,58,237,0.35)';
          off.lineWidth = lineBase;
          off.stroke();
        }

        if (this.iconImg && this.iconImg.complete) {
          const innerR = Math.max(0, baseStart - lineBase * 2);
          const maxDiam = Math.max(24, Math.min(minDim * 0.3, innerR * 2 * 0.3));
          const iw = this.iconImg.naturalWidth || 1;
          const ih = this.iconImg.naturalHeight || 1;
          const scale = maxDiam / Math.max(iw, ih);
          const drawW = Math.max(1, Math.floor(iw * scale));
          const drawH = Math.max(1, Math.floor(ih * scale));
          off.save();
          off.globalCompositeOperation = 'source-over';
          off.imageSmoothingEnabled = true;
          off.drawImage(this.iconImg, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
          off.restore();
        }
      } // end heavy redraw block
    }

    // Always clear and blit last rendered frame (prevents default bars + blinking)
    ctx.clearRect(0, 0, W, H);
    if (this._offscreen) ctx.drawImage(this._offscreen, 0, 0, W, H);
  }
}

export default AudioVisualizer;